
import React from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { Navigation } from "@/components/Navigation";
import { Features } from "@/components/landing/Features";
import { Footer } from "@/components/landing/Footer";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const FeaturesPage: React.FC = () => {
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();

  const getTitle = () => {
    switch(language) {
      case 'pt': return "TrainerFOLIO - Funcionalidades";
      case 'es': return "TrainerFOLIO - Características";
      case 'fr': return "TrainerFOLIO - Fonctionnalités";
      default: return "TrainerFOLIO - Features";
    }
  };

  const getDescription = () => {
    switch(language) {
      case 'pt': return "Explore as funcionalidades da plataforma TrainerFOLIO para Personal Trainers e Alunos.";
      case 'es': return "Explore las características de la plataforma TrainerFOLIO para Entrenadores Personales y Estudiantes.";
      case 'fr': return "Explorez les fonctionnalités de la plateforme TrainerFOLIO pour Personal Trainers et Élèves.";
      default: return "Explore TrainerFOLIO platform features for Personal Trainers and Students.";
    }
  };

  const getHomeText = () => {
    switch(language) {
      case 'pt': return 'Início';
      case 'es': return 'Inicio';
      case 'fr': return 'Accueil';
      default: return 'Home';
    }
  };

  const getFeaturesText = () => {
    switch(language) {
      case 'pt': return 'Funcionalidades';
      case 'es': return 'Características';
      case 'fr': return 'Fonctionnalités';
      default: return 'Features';
    }
  };

  const getDescriptionText = () => {
    switch(language) {
      case 'pt': return 'Conheça todas as funcionalidades que tornam o TrainerFOLIO a melhor plataforma para Personal Trainers.';
      case 'es': return 'Conozca todas las características que hacen de TrainerFOLIO la mejor plataforma para Entrenadores Personales.';
      case 'fr': return 'Découvrez toutes les fonctionnalités qui font de TrainerFOLIO la meilleure plateforme pour les Personal Trainers.';
      default: return 'Discover all the features that make TrainerFOLIO the best platform for Personal Trainers.';
    }
  };

  const getStartedText = () => {
    switch(language) {
      case 'pt': return 'Começar agora';
      case 'es': return 'Comenzar ahora';
      case 'fr': return 'Commencer maintenant';
      default: return 'Get started';
    }
  };

  // Schema.org data
  const schemaData = {
    name: getTitle(),
    description: getDescription(),
    provider: {
      "@type": "Organization",
      name: "TrainerFOLIO",
      url: "https://trainerfolio.com"
    },
    url: `https://trainerfolio.com${getPath('/features')}`,
    serviceType: language === 'pt' ? "Software de Fitness" : language === 'es' ? "Software de Fitness" : language === 'fr' ? "Logiciel de Fitness" : "Fitness Software"
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead 
        title={getTitle()}
        description={getDescription()}
        canonicalPath={getPath('/features')}
        schemaType="Service"
        schemaData={schemaData}
      />

      <header>
        <Navigation />
      </header>

      <main className="flex-grow bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 mt-20">
          <nav className="flex items-center text-sm text-slate-500 dark:text-slate-400">
            <Link to={getPath('/')} className="hover:text-flame">
              {getHomeText()}
            </Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {getFeaturesText()}
            </span>
          </nav>
        </div>

        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-black dark:text-white">
            {getFeaturesText()}
          </h1>
          <p className="mt-4 text-xl text-slate-600 dark:text-slate-400 max-w-3xl">
            {getDescriptionText()}
          </p>

          <Button asChild className="mt-6">
            <Link to={getPath('/auth')}>
              {getStartedText()}
            </Link>
          </Button>
        </div>

        {/* Features Section */}
        <Features />
      </main>

      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default FeaturesPage;
